import { useState, useEffect, useCallback, useRef } from 'react';
import { useSocket } from './useSocket';
import { axios } from './useAuth';

/**
 * useLyrics
 * Manages lyric synchronization via Socket.io
 */
export function useLyrics({ songId, roomId, currentTime, isPlaying }) {
  const [lyrics, setLyrics] = useState([]);
  const [currentLyricIndex, setCurrentLyricIndex] = useState(-1);
  const [currentLyric, setCurrentLyric] = useState(null);
  const [nextLyric, setNextLyric] = useState(null);
  const [loading, setLoading] = useState(false);
  const { socket } = useSocket();
  const lastEmitRef = useRef(0);

  // Load lyrics for this song
  useEffect(() => {
    if (!songId) return;
    setLoading(true);

    // If we have a socket and room, load via socket (gets pushed)
    if (socket && roomId) {
      socket.emit('lyrics:load', { songId, roomId });
      socket.on('lyrics:loaded', ({ lyrics: loaded }) => {
        setLyrics(loaded);
        setLoading(false);
      });
      return () => socket.off('lyrics:loaded');
    }

    // Fallback: load via HTTP
    axios.get(`/api/songs/${songId}/lyrics`)
      .then(res => {
        setLyrics(res.data.lyrics || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [songId, roomId, socket]);

  // Listen for pushed lyric sync events from server
  useEffect(() => {
    if (!socket || !roomId) return;

    const handleSync = ({ currentLyricIndex: idx, currentLyric: cur, nextLyric: nxt }) => {
      setCurrentLyricIndex(idx);
      setCurrentLyric(cur);
      setNextLyric(nxt);
    };

    socket.on('lyrics:sync', handleSync);
    return () => socket.off('lyrics:sync', handleSync);
  }, [socket, roomId]);

  // Emit time updates to server (throttled to 200ms)
  useEffect(() => {
    if (!socket || !roomId || !isPlaying) return;

    const now = Date.now();
    if (now - lastEmitRef.current < 200) return;
    lastEmitRef.current = now;

    socket.emit('audio:timeupdate', { roomId, currentTime, isPlaying });
  }, [socket, roomId, currentTime, isPlaying]);

  // Local fallback: compute current lyric from time if no socket sync
  useEffect(() => {
    if (!lyrics.length) return;
    if (socket && roomId) return; // Server handles this

    const idx = findCurrentLyricIndex(lyrics, currentTime);
    if (idx !== currentLyricIndex) {
      setCurrentLyricIndex(idx);
      setCurrentLyric(idx >= 0 ? lyrics[idx] : null);
      setNextLyric(idx >= 0 && idx + 1 < lyrics.length ? lyrics[idx + 1] : null);
    }
  }, [currentTime, lyrics, currentLyricIndex, socket, roomId]);

  return { lyrics, currentLyricIndex, currentLyric, nextLyric, loading };
}

function findCurrentLyricIndex(lyrics, currentTime) {
  let result = -1;
  for (let i = 0; i < lyrics.length; i++) {
    if (lyrics[i].startTime <= currentTime) {
      if (!lyrics[i].endTime || lyrics[i].endTime > currentTime) {
        result = i;
      } else {
        result = i;
      }
    } else {
      break;
    }
  }
  return result;
}
